// LigoPay — Smart search modal
// Pattern: Linear / Notion / Stripe-style searchable command palette.

document.addEventListener('DOMContentLoaded', () => {
  const modal = document.getElementById('ligo-search')
  if (!modal) return

  const input = modal.querySelector('[data-search-input]')
  const body = modal.querySelector('[data-search-body]')
  const states = {
    empty:      modal.querySelector('[data-search-state="empty"]'),
    loading:    modal.querySelector('[data-search-state="loading"]'),
    results:    modal.querySelector('[data-search-state="results"]'),
    'no-results': modal.querySelector('[data-search-state="no-results"]'),
  }
  const queryEl = modal.querySelector('[data-search-query]')
  const config = window.ligoSearchConfig || {}
  const minChars = config.minChars || 2

  let debounceId = null
  let activeRequest = null
  let activeIndex = -1
  let resultLinks = []

  // ── Helpers ────────────────────────────────────────────────────────────────

  const showState = (state) => {
    Object.entries(states).forEach(([key, el]) => {
      if (!el) return
      el.hidden = key !== state
    })
  }

  const escapeHtml = (s) => String(s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;')
    .replace(/>/g, '&gt;').replace(/"/g, '&quot;')

  const highlight = (text, query) => {
    if (!query) return escapeHtml(text)
    const escaped = escapeHtml(text)
    const safeQ = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    return escaped.replace(new RegExp(`(${safeQ})`, 'gi'), '<mark>$1</mark>')
  }

  const iconFor = (icon) => {
    const icons = {
      page:  '<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/>',
      news:  '<path d="M4 22h16a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2H8a2 2 0 0 0-2 2v16a2 2 0 0 1-2 2zm0 0a2 2 0 0 1-2-2v-9c0-1.1.9-2 2-2h2"/><path d="M18 14h-8"/><path d="M15 18h-5"/><path d="M10 6h8v4h-8z"/>',
      help:  '<circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/>',
    }
    return `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">${icons[icon] || icons.page}</svg>`
  }

  // ── Open / Close ───────────────────────────────────────────────────────────

  const open = () => {
    modal.classList.add('is-open')
    modal.setAttribute('aria-hidden', 'false')
    document.body.style.overflow = 'hidden'
    setTimeout(() => input.focus(), 60)
  }

  const close = () => {
    modal.classList.remove('is-open')
    modal.setAttribute('aria-hidden', 'true')
    document.body.style.overflow = ''
    activeIndex = -1
  }

  document.querySelectorAll('[data-search-open]').forEach((btn) => {
    btn.addEventListener('click', open)
  })

  modal.querySelectorAll('[data-search-close]').forEach((btn) => {
    btn.addEventListener('click', close)
  })

  // Cmd+K / Ctrl+K shortcut + Esc to close
  document.addEventListener('keydown', (e) => {
    const isOpen = modal.classList.contains('is-open')

    if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
      e.preventDefault()
      isOpen ? close() : open()
      return
    }

    if (!isOpen) return

    if (e.key === 'Escape') {
      e.preventDefault()
      close()
    } else if (e.key === 'ArrowDown') {
      e.preventDefault()
      moveActive(1)
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      moveActive(-1)
    } else if (e.key === 'Enter' && activeIndex >= 0) {
      e.preventDefault()
      const link = resultLinks[activeIndex]
      if (link) link.click()
    }
  })

  const moveActive = (dir) => {
    if (!resultLinks.length) return
    activeIndex = (activeIndex + dir + resultLinks.length) % resultLinks.length
    resultLinks.forEach((el, i) => {
      el.classList.toggle('is-active', i === activeIndex)
      if (i === activeIndex) el.scrollIntoView({ block: 'nearest' })
    })
  }

  // ── Search ─────────────────────────────────────────────────────────────────

  const renderResults = (data, query) => {
    const grouped = data.results || {}
    const total = data.total || 0

    if (total === 0) {
      if (queryEl) queryEl.textContent = `"${query}"`
      showState('no-results')
      resultLinks = []
      return
    }

    let html = ''
    Object.entries(grouped).forEach(([type, group]) => {
      html += `<section class="ligo-search__group">
        <header class="ligo-search__group-label">
          <span class="ligo-search__group-icon">${iconFor(group.icon)}</span>
          <span>${escapeHtml(group.label)}</span>
          <span class="ligo-search__group-count">${group.items.length}</span>
        </header>
        <ul class="ligo-search__list">`

      group.items.forEach((item) => {
        const titleHtml = highlight(item.title, query)
        const excerptHtml = highlight(item.excerpt, query)

        let metaHtml = ''
        if (item.extra && item.extra.category) {
          metaHtml += `<span class="ligo-search__chip-meta">${escapeHtml(item.extra.category)}</span>`
        }
        if (item.extra && item.extra.date) {
          metaHtml += `<time>${escapeHtml(item.extra.date)}</time>`
        }

        html += `<li>
          <a href="${escapeHtml(item.url)}" class="ligo-search__result" data-result-link>
            <div class="ligo-search__result-text">
              <strong>${titleHtml}</strong>
              <p>${excerptHtml}</p>
              ${metaHtml ? `<div class="ligo-search__result-meta">${metaHtml}</div>` : ''}
            </div>
            <svg class="ligo-search__result-arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="5" y1="12" x2="19" y2="12"/>
              <polyline points="12 5 19 12 12 19"/>
            </svg>
          </a>
        </li>`
      })

      html += `</ul></section>`
    })

    states.results.innerHTML = html
    showState('results')

    resultLinks = Array.from(states.results.querySelectorAll('[data-result-link]'))
    activeIndex = -1
  }

  const performSearch = (query) => {
    if (!query || query.length < minChars) {
      showState('empty')
      resultLinks = []
      return
    }

    showState('loading')

    if (activeRequest) activeRequest.abort()
    const ctrl = new AbortController()
    activeRequest = ctrl

    const url = `${config.ajaxUrl}?action=ligo_search&nonce=${encodeURIComponent(config.nonce)}&q=${encodeURIComponent(query)}`

    fetch(url, { signal: ctrl.signal, credentials: 'same-origin' })
      .then((r) => r.json())
      .then((res) => {
        if (!res.success) {
          showState('no-results')
          return
        }
        renderResults(res.data, query)
      })
      .catch((err) => {
        if (err.name !== 'AbortError') {
          console.error('Search failed:', err)
        }
      })
  }

  input.addEventListener('input', (e) => {
    const q = e.target.value.trim()
    clearTimeout(debounceId)
    debounceId = setTimeout(() => performSearch(q), 200)
  })
})
